import React, {useMemo, useState} from 'react';
import {Badge, TextInput} from '@mantine/core';
import {IconSearch} from '@tabler/icons-react';
import type {MRT_ColumnDef} from 'mantine-react-table';
import {SkillSignOffResultEnum, type SkillSignOffResponse} from 'src/api/ProactApiClient';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {useGetSkillSignOffRecordsQuery} from 'src/redux/queries/skillSignOff';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {parseUtcDate} from 'src/shared/utils/parseUtcDate';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const RESULT_PILL: Record<SkillSignOffResultEnum, {text: string; color: string}> = {
  [SkillSignOffResultEnum.Invalid]: {text: 'Unknown', color: 'gray'},
  [SkillSignOffResultEnum.SignedOff]: {text: 'Signed off', color: 'green'},
  [SkillSignOffResultEnum.NotYetCompetent]: {text: 'Not yet competent', color: 'red'},
};

/**
 * Every evaluated sign-off, newest first — the record of what happened,
 * including "Not yet competent" outcomes that are otherwise invisible once
 * a failed demonstration rolls off My Queue/Oversight (both pending-only).
 * Row action opens the read-only detail view; no row-click navigation per
 * convention.
 */
export const SignOffRecordsTab: React.FC = () => {
  const navigate = useAppNavigate();
  const [search, setSearch] = useState('');
  const {data, isLoading, isFetching, isError, error, refetch} =
    useGetSkillSignOffRecordsQuery({
      start: 0,
      length: 1000,
      search: search.trim() || undefined,
      mineOnly: false,
    });
  const rows = data ?? [];

  const columns = useMemo<MRT_ColumnDef<SkillSignOffResponse>[]>(
    () => [
      {accessorKey: 'userName', header: 'Trainee'},
      {accessorKey: 'employeeId', header: 'Employee ID'},
      {accessorKey: 'skillName', header: 'Skill'},
      {
        id: 'result',
        header: 'Result',
        accessorFn: row => String(row.result ?? SkillSignOffResultEnum.Invalid),
        filterVariant: 'select',
        mantineFilterSelectProps: {
          data: [
            {
              value: String(SkillSignOffResultEnum.SignedOff),
              label: 'Signed off',
            },
            {
              value: String(SkillSignOffResultEnum.NotYetCompetent),
              label: 'Not yet competent',
            },
          ],
        },
        Cell: ({row}) => {
          const pill =
            RESULT_PILL[row.original.result ?? SkillSignOffResultEnum.Invalid];
          return <Badge color={pill.color}>{pill.text}</Badge>;
        },
      },
      {
        accessorKey: 'signedOffByName',
        header: 'Evaluated by',
        Cell: ({cell}) => cell.getValue<string | undefined>() ?? '—',
      },
      {
        accessorKey: 'signedOffAt',
        header: 'Evaluated',
        Cell: ({cell}) => {
          const value = cell.getValue<Date | undefined>();
          return value ? parseUtcDate(value).toLocaleDateString() : '—';
        },
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier: 'training-sign-off-records',
    columns,
    defaultDensity: 'xs',
    defaultPagination: {pageIndex: 0, pageSize: 20},
    defaultSorting: [{id: 'signedOffAt', desc: true}],
  });

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: false,
      manualFiltering: false,
      manualPagination: false,
      columns,
      data: rows,
      getRowId: row => String(row.id),
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.View
            tooltip="View"
            onClick={() =>
              navigate(
                PathUtility.TrainingSignOffDetail(String(row.original.id))
              )
            }
          />
        </TableRowActions>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Filters>
            <TextInput
              placeholder="Search trainee, employee ID or skill"
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={event => setSearch(event.currentTarget.value)}
              w={280}
            />
          </TableToolbar.Filters>
        </TableToolbar>
      ),
    },
    totalCount: rows.length,
    isError,
    error: isError ? parseErrorMessage(error) : undefined,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return <>{table}</>;
};
